import { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import { LocalizationContext, UserContext } from '../contexts';
import { stringScoreNormalizer } from '../utils/helper';

const emptyTime = (new Date('0001-01-01T00:00:00')).getTime();

export const useRepositoryHook = () => {
    const history = useHistory();
    const [localization] = useContext(LocalizationContext);
    const [user] = useContext(UserContext);

    const [disciplines, setDisciplines] = useState(false);
    const [itemTypes, setItemTypes] = useState([]);
    const [itemSources, setItemSources] = useState([]);
    const [itemContexts, setItemContexts] = useState([]);
    const [ageGrades, setAgeGrades] = useState([]);

    const [isItemsLoading, setIsItemsLoading] = useState(false);
    const [errorItemsLoading, setErrorItemsLoading] = useState(null);
    const [items, setItems] = useState([]);

    const [isEditItemLoading, setIsEditItemLoading] = useState(false);
    const [errorEditItemLoading, setErrorEditItemLoading] = useState(null);
    const [editItem, setEditItem] = useState(false);

    const [isEditItemDimensionsLoading, setIsEditItemDimensionsLoading] = useState(false);
    const [errorEditItemDimensionsLoading, setErrorEditItemDimensionsLoading] = useState(null);
    const [editItemDimensions, setEditItemDimensions] = useState(false);

    const [isItemDeleting, setIsItemDeleting] = useState(false);
    const [errorItemDeleting, setErrorItemDeleting] = useState(null);

    const [searchDisciplineID, setSearchDisciplineID] = useState(false);
    const [searchLanguageID, setSearchLanguageID] = useState(false);

    const { isLoading, isError, data, error } = useQuery('repository_dimensions', () =>
        window.cet.microservices.contentapi.content.getAllDimensionsAndDependencies({}),
        {
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchOnMount: false
        }
    );

    useEffect(() => {
        const dimensions = data && data.dimensions;
        if (dimensions?.disciplines) {
            let newDisciplines = [];
            Object.keys(dimensions.disciplines).forEach(key => {
                newDisciplines.push(dimensions.disciplines[key]);
            });
            setDisciplines(newDisciplines);
        }
        else {
            setDisciplines(false);
        }

        if (dimensions?.itemTypes) {
            let newItemTypes = [];
            Object.keys(dimensions.itemTypes).forEach(key => {
                newItemTypes.push(dimensions.itemTypes[key]);
            });
            setItemTypes(newItemTypes);
        }
        else {
            setItemTypes([]);
        }

        if (dimensions?.itemSource) {
            let newItemSources = [];
            Object.keys(dimensions.itemSource).forEach(key => {
                newItemSources.push(dimensions.itemSource[key]);
            });
            setItemSources(newItemSources);
        }
        else {
            setItemSources([]);
        }

        if (dimensions?.itemContext) {
            let newItemContexts = [];
            Object.keys(dimensions.itemContext).forEach(key => {
                newItemContexts.push(dimensions.itemContext[key]);
            });
            setItemContexts(newItemContexts);
        }
        else {
            setItemContexts([]);
        }

        if (dimensions?.ageGrades) {
            let newAgeGrades = [];
            Object.keys(dimensions.ageGrades).forEach(key => {
                newAgeGrades.push(dimensions.ageGrades[key]);
            });
            setAgeGrades(newAgeGrades);
        }
        else {
            setAgeGrades([]);
        }
    }, [data]);

    useEffect(() => {
        loadItemsByDisciplineApi();
    }, [disciplines]);

    useEffect(() => {
        loadItemsByDisciplineApi();
    }, [searchDisciplineID, searchLanguageID]);

    const loadItemsByDiscipline = (disciplineID, languageID) => {
        setSearchDisciplineID(disciplineID);
        setSearchLanguageID(languageID);
    };

    const clearErrorItemsLoading = () => {
        setErrorItemsLoading(false);
    };

    const loadItemsByDisciplineApi = () => {
        if (searchDisciplineID && disciplines) {
            setIsItemsLoading(true);

            var searchParams = {
                "lang": localization.ui_language,
                "dimensionParams": {},
                "contentParams": {},
                "textParams": {},
                "retrieveParams": {},
                "skipCache": true
            };

            if (searchDisciplineID !== 'all') {
                searchParams.dimensionParams.disciplines = [searchDisciplineID];
            }

            if (searchLanguageID !== 'all') {
                searchParams.contentParams.contextLanguage = [searchLanguageID];
            }

            window.cet.microservices.contentapi.search.search(searchParams).then(data => {
                var itemsList = data?.records?.all?.length > 0 ? data.records.all.map(item => {
                    var newItem = { ...item };
                    if (newItem.addedDate) {
                        newItem.addedDate = new Date(newItem.addedDate);
                        if (newItem.addedDate?.getTime() === emptyTime) {
                            newItem.addedDate = null;
                        }
                    }
                    if (newItem.lastUpdatedDate) {
                        newItem.lastUpdatedDate = new Date(newItem.lastUpdatedDate);
                        if (newItem.lastUpdatedDate?.getTime() === emptyTime) {
                            newItem.lastUpdatedDate = null;
                        }
                    }
                    if (newItem.lastCatalogPublishedDate) {
                        newItem.lastCatalogPublishedDate = new Date(newItem.lastCatalogPublishedDate);
                        if (newItem.lastCatalogPublishedDate?.getTime() === emptyTime) {
                            newItem.lastCatalogPublishedDate = null;
                        }
                    }
                    if (newItem.informaticApprovalDate) {
                        newItem.informaticApprovalDate = new Date(newItem.informaticApprovalDate);
                        if (newItem.informaticApprovalDate?.getTime() === emptyTime) {
                            newItem.informaticApprovalDate = null;
                        }
                    }
                    newItem.ageGrades = newItem.ageGrades ? Object.keys(newItem.ageGrades).map(key => newItem.ageGrades[key]).join(', ') : '';
                    newItem.itemTypes = newItem.itemTypes ? Object.keys(newItem.itemTypes).map(key => newItem.itemTypes[key]).join(', ') : '';
                    newItem.title = newItem.title ? newItem.title + '<!--###-->' + stringScoreNormalizer(newItem.title) : '';

                    if (newItem.itemSource) {
                        let source = (itemSources.find(itemSource => itemSource.id === newItem.itemSource));
                        newItem.itemSource = source ? source[localization.labels.repositoryManagement.itemSource_textField] : '';
                    }
                    else {
                        newItem.itemSource = '';
                    }
                    
                    if (newItem.itemContext) {
                        let context = (itemContexts.find(itemContext => itemContext.id === newItem.itemContext));
                        newItem.itemContext = context ? context[localization.labels.repositoryManagement.itemContext_textField] : '';
                    }
                    else {
                        newItem.itemSource = '';
                    }

                    return newItem;
                }) : [];
                setItems(itemsList);
                setIsItemsLoading(false);
            }).catch(error => {
                setErrorItemsLoading(error);
                setItems([]);
                setIsItemsLoading(false);
            });
        }
    };

    const getNairobiIdByExternalContextIdAndLang = async (externalContextId, languageID) => {
        if (!externalContextId) return null;

        var searchParams = {
            "lang": localization.ui_language,
            "dimensionParams": {},
            "contentParams": {},
            "textParams": {},
            "retrieveParams": {},
            "skipCache": true
        };

        searchParams.contentParams.externalContextId = [externalContextId];

        if (languageID) {
            searchParams.contentParams.contextLanguage = [languageID];
        }

        var result = null;

        await window.cet.microservices.contentapi.search.search(searchParams).then(data => {
            let itemsList = data?.records?.all;
            if (!itemsList || itemsList.length === 0) return null;
            result = itemsList[0].id;
        }).catch(error => {
            console.error(error);
        });

        return result;
    }

    const importItem = async (url, userName) => {
        let params = {
            "url": url,
            "userName": userName,
        };
        let result = null;

        await window.cet.microservices.contentapi.import.importItem(params).then(data => {
            result = data;
        }).catch(error => {
            console.error(error);
        });

        return result;
    }

    const getSectorsByDisciplines = async () => {
        let result = null;
        let params = {};

        await window.cet.microservices.contentapi.content.getSectorsByDisciplines(params).then(data => {
            result = data;
        }).catch(error => {
            console.error(error);
        });

        return result;
    }

    const getEditItem = (itemID) => {
        if (itemID === "new") {
            setIsEditItemLoading(false);
            setErrorEditItemLoading(false);
            setEditItem({ id: null });

            setIsEditItemDimensionsLoading(false);
            setErrorEditItemDimensionsLoading(error);
            setEditItemDimensions({});
        }
        else if (itemID) {
            setIsEditItemLoading(true);
            setIsEditItemDimensionsLoading(true);

            window.cet.microservices.contentapi.content.getItemById({ itemId: itemID }).then(async item => {
                setIsEditItemLoading(false);
                item.additionalInfo = item.additionalInfo ? JSON.parse(item.additionalInfo) : {};
                let lang = item.contextLanguage === 'he' ? 'ar' : 'he'; //Future TODO: add English support                
                await getNairobiIdByExternalContextIdAndLang(item.externalContextId, lang).then(otherId => {
                    item.otherLangItemId = otherId;
                });
                setErrorEditItemLoading(false);
                setEditItem(item);
            }).catch(error => {
                setIsEditItemLoading(false);
                setErrorEditItemLoading(error);
                setEditItem(false);
            });

            window.cet.microservices.contentapi.content.getItemDimensions({ itemId: itemID }).then(item => {
                setIsEditItemDimensionsLoading(false);
                setErrorEditItemDimensionsLoading(false);
                setEditItemDimensions(item);
            }).catch(error => {
                setIsEditItemDimensionsLoading(false);
                setErrorEditItemDimensionsLoading(error);
                setEditItemDimensions(false);
            });
        }
        else {
            setIsEditItemLoading(false);
            setErrorEditItemLoading(false);
            setEditItem(false);

            setIsEditItemDimensionsLoading(false);
            setErrorEditItemDimensionsLoading(error);
            setEditItemDimensions(false);
        }
    };

    const clearErrorEditItemLoading = () => {
        setErrorEditItemLoading(false);
        setErrorEditItemDimensionsLoading(false);
    };

    const createItem = () => {
        var time = (new Date()).toISOString();
        var new_item = {
            "id": "",
            "isMobileSupported": false,
            "addedDate": time,
            "addedUser": "",
            "lastPublishedDate": null,
            "lastUpdatedDate": time,
            "lastUpdatedUser": "",
            "itemSource": "",
            "itemContext": "",
            "externalContextId": "",
            "contextLanguage": "",
            "contextURL": "",
            "title": "",
            "subtitle": "",
            "summary": "",
            "duration": 0,
            "isPublished": false,
            "doNotImport": false,
            "isNeedsPayment": false,
            "itemImage": "",
            "totalNumOfQuestions": 0,
            "numOfAutomaticQuestions": 0,
            "additionalInfo": null,
            "lastCatalogPublishedDate": false,
            "informaticApprovalDate": false,
            "isInformaticApproved": false,
            "designApproved": false,
            "otherLangItemId ": null
        };        

        setIsEditItemLoading(true);
        setIsEditItemDimensionsLoading(true);
        window.cet.microservices.contentapi.content.createItem(new_item).then(itemId => {
            setIsEditItemLoading(false);
            setIsEditItemDimensionsLoading(null);
            history.push('/repository/' + searchDisciplineID + '/' + itemId);
        }).catch(error => {
            setIsEditItemLoading(false);
            setIsEditItemDimensionsLoading(error);
        });
    };

    const updateItem = (item, itemDisciplines, thumbnail) => {
        const updatePros = new window.Promise((resolve, reject) => {
            var _item = { ...item };
            if (user && user.displayName) {
                _item.lastUpdatedUser = user.displayName;
            }
            _item.additionalInfo = _item.additionalInfo ? JSON.stringify(_item.additionalInfo) : "";

            window.cet.microservices.contentapi.content.updateItemAndDimensions({
                Content: _item,
                Dimensions: itemDisciplines
            }, thumbnail).then(updatedData => {
                updatedData.content.additionalInfo = updatedData.content.additionalInfo ? JSON.parse(updatedData.content.additionalInfo) : {};
                setEditItem({ ...(updatedData.content) });
                setEditItemDimensions({ ...(updatedData.dimensions) });

                var old_item = items.filter(i => i.id === updatedData.content.id);
                old_item = old_item.length > 0 ? old_item[0] : false;

                var new_item = {
                    addedDate: updatedData.content.addedDate ? new Date(updatedData.content.addedDate) : null,
                    ageGrades: updatedData.dimensions.ageGrades && data && data.dimensions && data.dimensions.ageGrades ? updatedData.dimensions.ageGrades.map(key => data.dimensions.ageGrades[key][localization.labels.repositoryManagement.ageGrade_textField]).join(', ') : '',
                    id: updatedData.content.id,
                    itemTypes: updatedData.dimensions.itemTypes && data && data.dimensions && data.dimensions.itemTypes ? updatedData.dimensions.itemTypes.map(key => data.dimensions.itemTypes[key][localization.labels.repositoryManagement.itemType_textField]).join(', ') : '',
                    lastUpdatedDate: updatedData.content.lastUpdatedDate ? new Date(updatedData.content.lastUpdatedDate) : null,
                    lastCatalogPublishedDate: updatedData.content.lastCatalogPublishedDate ? new Date(updatedData.content.lastCatalogPublishedDate) : null,
                    informaticApprovalDate: updatedData.content.informaticApprovalDate ? new Date(updatedData.content.informaticApprovalDate) : null,
                    title: updatedData.content.title,
                    isPublished: updatedData.content.isPublished,
                    designApproved: updatedData.content.designApproved
                };
                if (new_item.addedDate?.getTime() === emptyTime) {
                    new_item.addedDate = null;
                }
                if (new_item.lastUpdatedDate?.getTime() === emptyTime) {
                    new_item.lastUpdatedDate = null;
                }
                if (new_item.lastCatalogPublishedDate?.getTime() === emptyTime) {
                    new_item.lastCatalogPublishedDate = null;
                }
                if (new_item.informaticApprovalDate?.getTime() === emptyTime) {
                    new_item.informaticApprovalDate = null;
                }
                new_item.title = new_item.title ? new_item.title + '<!--###-->' + stringScoreNormalizer(new_item.title) : '';

                let isInList = searchDisciplineID === 'all' || (updatedData.dimensions.disciplines?.indexOf(searchDisciplineID) >= 0);

                if (!old_item && !isInList) {

                }
                else if (!old_item && isInList) {
                    setItems([new_item, ...items]);
                }
                else if (old_item && !isInList) {
                    setItems(([...items]).filter(i => i.id !== new_item.id));
                }
                else if (old_item && isInList) {
                    setItems(([...items]).map(i => {
                        if (i.id === new_item.id) {
                            return new_item;
                        }
                        else {
                            return i;
                        }
                    }));
                }

                resolve(updatedData);
            }).catch(error => {
                reject(error);
            });
        });
        return updatePros;
    };

    const deleteItem = (itemId) => {
        setIsItemDeleting(true);
        setErrorItemDeleting(null);
        window.cet.microservices.contentapi.content.deleteItem({
            itemId: itemId
        }).then(() => {
            setItems({ ...items }.filter(i => i.id !== itemId));
            setIsItemDeleting(false);
            setErrorItemDeleting(null);
            if (editItem?.id === itemId) {
                history.push('/repository/' + searchDisciplineID);
            }
        }).catch(error => {
            setIsItemDeleting(false);
            setErrorItemDeleting(error);
        });
    };

    const clearErrorItemDeleting = () => {
        setErrorItemDeleting(null);
    };

    return {
        isLoadingDimensions: isLoading,
        isErrorDimensions: isError,
        dimensions: data,
        dimensionsError: error,

        disciplines: disciplines,
        itemTypes: itemTypes,
        itemSources: itemSources,
        itemContexts: itemContexts,
        ageGrades: ageGrades,
        loadItemsByDiscipline: loadItemsByDiscipline,
        isItemsLoading: isItemsLoading,
        errorItemsLoading: errorItemsLoading,
        items: items,
        clearErrorItemsLoading: clearErrorItemsLoading,

        isEditItemLoading: isEditItemLoading || isEditItemDimensionsLoading,
        errorEditItemLoading: errorEditItemLoading || errorEditItemDimensionsLoading,
        clearErrorEditItemLoading: clearErrorEditItemLoading,
        editItem: editItem,
        editItemDimensions: editItemDimensions,
        getEditItem: getEditItem,

        updateItem: updateItem,
        deleteItem: deleteItem,
        isItemDeleting: isItemDeleting,
        errorItemDeleting: errorItemDeleting,
        clearErrorItemDeleting: clearErrorItemDeleting,
        createItem: createItem,
        importItem: importItem,
        getSectorsByDisciplines: getSectorsByDisciplines
    };
};